import React from 'react';
import { Maximize2, Minimize2 } from 'lucide-react';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from '../ui/dialog';
import CustomFieldBadge from '../custom-fields/CustomFieldBadge';

/**
 * The Preview Data table of the import wizard (#973), shared by the inline
 * preview and the expanded view so both read the same rows the same way.
 *
 * `columns` is [{ key, label, isCustom }] in the order to show; `rows` are the
 * preview's sample rows. Cells render `staged_values[key]` as TEXT — never
 * HTML — so a value such as `<b>x</b>` is shown literally.
 *
 * - `inline`: the wizard's own table, one line per row (unchanged size).
 * - `expanded`: scrolls both ways inside the dialog; the header row sticks to
 *   the top and the Entity column to the left, both with opaque backgrounds
 *   (the same rules as LmTable's HEAD_CELL / PIN_NAME). Long values wrap at
 *   320px and carry their full text as a title.
 */
export function PreviewTable({ columns, rows, variant = 'inline' }) {
    const expanded = variant === 'expanded';
    const cell = (r, key) => {
        const v = r.staged_values?.[key];
        return v === null || v === undefined ? '' : String(v);
    };

    if (!expanded) {
        return (
            <div className="mt-[18px] border border-border rounded-xl overflow-x-auto" data-testid="lm-preview-table">
                <table className="border-collapse w-full min-w-[760px]">
                    <thead>
                        <tr className="bg-muted/50">
                            <th scope="col" className="px-3.5 py-2.5 border-b border-border text-left text-[11.5px] font-semibold text-ink-2 whitespace-nowrap">Entity</th>
                            {columns.map((c) => (
                                <th key={c.key} scope="col" className="px-3.5 py-2.5 border-b border-border text-left text-[11.5px] font-semibold text-ink-2 whitespace-nowrap">
                                    {c.label}
                                    {c.isCustom && <CustomFieldBadge className="ml-1.5" />}
                                </th>
                            ))}
                            <th scope="col" className="px-3.5 py-2.5 border-b border-border text-left text-[11.5px] font-semibold text-ink-2 whitespace-nowrap">Match</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.row_number} className="border-b border-border/60">
                                <td className="px-3.5 py-2.5 text-[12.5px] text-foreground">{r.entity_type || '—'}</td>
                                {columns.map((c) => (
                                    <td key={c.key} className="px-3.5 py-2.5 text-[12.5px] text-foreground whitespace-nowrap">{cell(r, c.key)}</td>
                                ))}
                                <td className="px-3.5 py-2.5 text-[12.5px] text-ink-2">{r.match_status || '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    // Separate borders: a sticky cell keeps its own border, which collapsed
    // borders would lose while scrolling.
    const head = 'sticky top-0 bg-muted border-b border-border px-3.5 py-2.5 text-left align-middle text-[11.5px] font-semibold text-ink-2 whitespace-nowrap';
    const pinHead = `${head} left-0 z-30 shadow-[1px_0_0_hsl(var(--border))]`;
    const body = 'px-3.5 py-2.5 border-b border-border align-top text-[12.5px] text-foreground';
    return (
        <div className="flex-1 min-h-0 overflow-auto" data-testid="lm-preview-expanded-scroll">
            <table className="border-separate border-spacing-0 min-w-full">
                <thead>
                    <tr>
                        <th scope="col" className={pinHead}>Entity</th>
                        {columns.map((c) => (
                            <th key={c.key} scope="col" className={`${head} z-20`}>
                                {c.label}
                                {c.isCustom && <CustomFieldBadge className="ml-1.5" />}
                            </th>
                        ))}
                        <th scope="col" className={`${head} z-20`}>Match</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.row_number}>
                            <td className={`${body} sticky left-0 z-10 bg-background whitespace-nowrap shadow-[1px_0_0_hsl(var(--border))]`}>{r.entity_type || '—'}</td>
                            {columns.map((c) => {
                                const text = cell(r, c.key);
                                return (
                                    <td key={c.key} title={text || undefined}
                                        className={`${body} max-w-[320px] min-w-[120px] whitespace-normal break-words`}>
                                        {text || <span className="text-muted-foreground">—</span>}
                                    </td>
                                );
                            })}
                            <td className={`${body} whitespace-nowrap text-muted-foreground`}>{r.match_status || '—'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/**
 * Expand: every mapped column of the preview's sample rows in a dialog of its
 * own, wider than the 1060px wizard. It is a pure view — it holds no preview or
 * policy state and calls nothing — so opening and closing it never rebuilds the
 * preview or changes duplicate handling (#973). DialogTrigger returns focus to
 * the button on close; Esc and Close both close it.
 */
export default function LmPreviewExpanded({ columns, rows, fileName }) {
    const n = columns.length;
    return (
        <Dialog>
            <DialogTrigger asChild>
                <button type="button" data-testid="lm-preview-expand"
                        className="shrink-0 inline-flex items-center gap-1.5 h-8 px-3 border border-input rounded-lg bg-card text-[12.5px] font-medium text-foreground hover:bg-muted">
                    <Maximize2 className="w-3.5 h-3.5" aria-hidden="true" />Expand
                </button>
            </DialogTrigger>
            <DialogContent
                data-testid="lm-preview-expanded" aria-modal="true"
                className="max-w-[calc(100vw-40px)] w-[calc(100vw-40px)] h-[calc(100vh-40px)] max-h-[calc(100vh-40px)] p-0 gap-0 overflow-hidden flex flex-col rounded-[14px] [&>button.absolute]:hidden">
                <div className="shrink-0 flex items-start gap-3 px-5 py-4 border-b border-border bg-muted">
                    <div className="flex-1 min-w-0">
                        <DialogTitle className="text-[17px] font-semibold tracking-[-0.01em]">Preview data · all columns</DialogTitle>
                        <DialogDescription className="mt-1 text-[12.5px] tabular-nums text-ink-2">
                            {rows.length} sample row{rows.length === 1 ? '' : 's'} · {n} mapped column{n === 1 ? '' : 's'}{fileName ? ` · ${fileName}` : ''}
                        </DialogDescription>
                    </div>
                    <span className="hidden sm:inline text-[12px] text-ink-2 pt-1.5">Esc to close</span>
                    <DialogClose asChild>
                        <button type="button" data-testid="lm-preview-expanded-close"
                                className="shrink-0 inline-flex items-center gap-1.5 h-8 px-3 border border-border rounded-lg bg-background text-[12.5px] font-medium hover:bg-accent">
                            <Minimize2 className="w-3.5 h-3.5" aria-hidden="true" />Close
                        </button>
                    </DialogClose>
                </div>
                <PreviewTable columns={columns} rows={rows} variant="expanded" />
                <div className="shrink-0 px-5 py-2.5 border-t border-border bg-muted text-[12px] text-ink-2">
                    Closing returns to the preview as it was. Duplicate handling and write policy do not change.
                </div>
            </DialogContent>
        </Dialog>
    );
}
